import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid2";
import MUITextField from "@mui/material/TextField";
import MUIAttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MUICalculateIcon from "@mui/icons-material/Calculate";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControlLabel from "@mui/material/FormControlLabel";

import Patient from "../../../Utility/Crud/Patient";
import PdfGoodFaithEstimate from "../../../Utility/Pdf/PdfGoodFaithEstimate";

import Card from "../../Card";
import InfoItem from "../../InfoItem";

import DialogPatientResponsibilityEstimator from "../../Dialog/DialogPatientResponsibilityEstimator";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Format from "../../../Utility/Format";

/**
 * CardChartFinancial renders a Card component displaying patient financial
 * information.
 * @returns {JSX.Element}
 */

const title = "Financial";
const icon = MUIAttachMoneyIcon;

const className = "CardChartFinancial";

export default function CardChartFinancial(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);
  const [
    openDialogPatientResponsibilityEstimator,
    setOpenDialogPatientResponsibilityEstimator,
  ] = useState(false);
  const [shouldSave, setShouldSave] = useState({
    shouldSave: false,
    shouldDownloadGoodFaithEstimate: false,
  });
  const [shouldDownloadGoodFaithEstimate, setShouldDownloadGoodFaithEstimate] =
    useState(false);

  const order = chartContext.chart.orderActivity[chartContext.orderId];
  const orderActivityApprovals = Object.values(
    chartContext.chart.orderActivityApprovals,
  ).find(
    (orderActivityApproval) =>
      orderActivityApproval.orderActivityId === chartContext.orderId,
  );

  //NOTE: Approval only exists for orders
  //If approval doesn't exist, create it with any values that updated
  const approval = Object.values(chartContext.chart?.approval || {}).find(
    (approval) => approval?.id === orderActivityApprovals?.approvalsId,
  );
  const approvalId = approval?.id;

  const originalApproval = chartContext.originalChart.approval[approvalId];

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
    setShouldSave({
      shouldSave: false,
      shouldDownloadGoodFaithEstimate: false,
    });
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Handles the saving of the order.
   * @returns {Promise} A promise that resolves when the order is saved.
   */
  useEffect(() => {
    if (!shouldSave?.shouldSave) {
      return;
    }

    if (!chartContext.hasPendingChanges) {
      setOpenDialogPatientResponsibilityEstimator(false);
      if (shouldSave.shouldDownloadGoodFaithEstimate) {
        setShouldDownloadGoodFaithEstimate(true);
        setShouldSave({
          shouldSave: false,
          shouldDownloadGoodFaithEstimate: false,
        });
      }
    } else {
      chartContext.handleSaveChart(null, async () => {
        setOpenDialogPatientResponsibilityEstimator(false);
        if (shouldSave.shouldDownloadGoodFaithEstimate) {
          setShouldDownloadGoodFaithEstimate(true);
        }
      });
    }
  }, [shouldSave]);

  useEffect(() => {
    if (!shouldDownloadGoodFaithEstimate) {
      return;
    }
    handleDownloadGoodFaithEstimate();
    setShouldDownloadGoodFaithEstimate(false);
    setShouldSave({
      shouldSave: false,
      shouldDownloadGoodFaithEstimate: false,
    });
  }, [shouldDownloadGoodFaithEstimate]);

  /**
   * Returns styles for a container that should be full width at a given max
   * width. Used for responsive container queries when sidebar is enabled.
   * @param {number} maxWidth - The max width for the container query.
   * @returns {Object} The style object for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  const getPatientResponsibilityRows = () => {
    const isProductDelivered = (chartProduct) => {
      const deliveryRows = Object.values(
        chartContext.chart.deliveryInformation || {},
      ).filter((row) => {
        if (
          row.documentType !== "Equipment Delivery" ||
          row.orderId !== chartContext.orderId
        ) {
          return false;
        }

        try {
          JSON.parse(row.documentInfoPruned);
          return true;
        } catch {
          return false;
        }
      });

      if (deliveryRows.length === 0) {
        return false;
      }

      deliveryRows.sort(
        (a, b) => new Date(b.uploadedOn) - new Date(a.uploadedOn),
      );

      let documentInfoPruned;
      try {
        documentInfoPruned = JSON.parse(deliveryRows[0].documentInfoPruned);
      } catch {
        return false;
      }

      if (!documentInfoPruned?.products) {
        return false;
      }

      const foundDeliveryProduct = documentInfoPruned.products.find(
        (deliveryProduct) => deliveryProduct.id === chartProduct.id,
      );
      return !!foundDeliveryProduct?.delivered;
    };

    const sortProductGroups = (groups, primaryProduct) => {
      return groups.sort((groupA, groupB) => {
        const mainA = groupA[groupA.length - 1];
        const mainB = groupB[groupB.length - 1];

        if (mainA.id === primaryProduct?.id) {
          return -1;
        }
        if (mainB.id === primaryProduct?.id) {
          return 1;
        }

        const pointsA = mainA.points || 0;
        const pointsB = mainB.points || 0;
        if (pointsB !== pointsA) {
          return pointsB - pointsA;
        }

        const typeA = mainA.productTypeId || 0;
        const typeB = mainB.productTypeId || 0;
        if (typeA !== typeB) {
          return typeA - typeB;
        }

        return mainA.id - mainB.id;
      });
    };

    const allProducts = OrderActivity.getProducts(
      order,
      chartContext.chart,
    ).filter((product) => !product.selectedUpgrade);
    const primaryProduct = OrderActivity.getPrimaryProduct(allProducts);
    const productMap = new Map(
      allProducts.map((product) => [product.id, product]),
    );

    const groupedProducts = [];
    const visitedIds = new Set();
    for (const product of allProducts) {
      if (visitedIds.has(product.id)) {
        continue;
      }

      if (product.upgradedFromProductId) {
        const parent = productMap.get(product.upgradedFromProductId);
        if (parent) {
          groupedProducts.push([product, parent]);
          visitedIds.add(product.id);
          visitedIds.add(parent.id);
          continue;
        }
      }

      groupedProducts.push([product]);
      visitedIds.add(product.id);
    }

    const deliveredGroups = [];
    const nonDeliveredGroups = [];
    groupedProducts.forEach((group) => {
      if (group.some((product) => isProductDelivered(product))) {
        deliveredGroups.push(group);
      } else {
        nonDeliveredGroups.push(group);
      }
    });

    const sortedProducts = [
      ...sortProductGroups(deliveredGroups, primaryProduct),
      ...sortProductGroups(nonDeliveredGroups, primaryProduct),
    ].flat();

    return sortedProducts
      .filter((product) => product.billable === true)
      .map((item) => {
        const hcpcsCode = item.hcpcs?.toUpperCase().substring(0, 5);
        const hcpcsMatch = Object.values(mainContext.hcpcsCodesIndexed).find(
          (hcpcs) => {
            return hcpcs.code === hcpcsCode;
          },
        );
        const modifierCode = hcpcsMatch
          ? mainContext.modifierCodesIndexed[1]
          : null;
        const feeSchedule = hcpcsMatch
          ? Object.values(mainContext.feeSchedulesIndexed).find(
              (feeSchedule) => {
                return (
                  feeSchedule.hcpcsCodeId === hcpcsMatch.id &&
                  feeSchedule.modifierCodeId === modifierCode.id
                );
              },
            )
          : null;
        const productSubTypeName = item.productSubTypeId
          ? mainContext.productSubTypesIndexed[item.productSubTypeId]?.name
          : null;
        return {
          ...item,
          hcpcs: hcpcsMatch || null,
          feeSchedule: feeSchedule || null,
          modifierCode: modifierCode || null,
          productSubTypeName: productSubTypeName || null,
        };
      });
  };

  const handleDownloadGoodFaithEstimate = async () => {
    const patient = chartContext.chart.patient[chartContext.patientId] || {};
    const rows = getPatientResponsibilityRows();

    const pdfGoodFaithEstimate = new PdfGoodFaithEstimate({
      chartContext,
      mainContext,
      rows,
    });
    await pdfGoodFaithEstimate.render();

    const patientName = Patient.getDisplayName(patient) || "Patient";
    const filename = `${patientName.replace(/[\/:*?"<>|]/g, "").replace(/\s+/g, "-")}_Good-Faith-Estimate.pdf`;
    pdfGoodFaithEstimate.document.save(filename);
  };

  /**
   * Formats a measurement value to two decimal places, or returns null if
   * invalid.
   * @param {string|number} value - The value to format.
   * @returns {number|null}
   */
  const formatDollarValue = (value) => {
    if (value === "" || value === null || isNaN(Number(value))) {
      return null;
    }
    return Math.round(Number(value) * 100) / 100;
  };

  const sanitizeInputDollarValue = (value) => {
    let newValue = value.replace(/[^0-9.]/g, "");
    const parts = newValue.split(".");
    if (parts.length > 2) {
      newValue = parts[0] + "." + parts.slice(2).join("");
    } else if (newValue === "") {
      newValue = null;
    }
    return newValue;
  };

  /**
   * Handles changes to dollar input fields, allowing only valid currency
   * input. Updates approval state and local cardChart state.
   * @param {string} field - The approval field being changed.
   * @param {string} value - The new value for the field.
   */
  const handleDollarInput = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = value.trim();
    const numValue = parseFloat(trimmed);
    if (/^[0-9]+\.[0-9]{0,2}$/.test(trimmed)) {
      return numValue;
    } else {
      return formatDollarValue(numValue);
    }
  };

  /**
   * Handles changes to dollar input fields, allowing only valid currency
   * input. Updates approval state and local cardChart state.
   * @param {string} field - The approval field being changed.
   * @param {string} value - The new value for the field.
   */
  const handleDollarInputChange = (field, value) => {
    chartContext.onChange({ approval: { [approvalId]: { [field]: value } } });
    setCardChart({ ...cardChart, [field]: handleDollarInput(value) });
  };

  /**
   * Handles blur event for dollar input fields, ensuring value is trimmed and
   * valid. Updates approval state.
   * @param {string} field - The approval field being blurred.
   * @param {string} value - The value to validate and set.
   */
  const handleDollarInputBlur = (field, value) => {
    const formattedValue = formatDollarValue(value);
    chartContext.onChange({
      approval: { [approvalId]: { [field]: formattedValue } },
    });
    setCardChart({ ...cardChart, [field]: formattedValue });
  };

  /**
   * Renders the deductible input field.
   * @returns {JSX.Element} Deductible input field.
   */
  const renderDeductibleInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={
            order?.insuranceTypeId === 4
              ? "N/A - Cash"
              : (approval?.deductible ?? "")
          }
          size="small"
          label="Deductible"
          //Cash
          disabled={order?.insuranceTypeId === 4}
          variant="standard"
          fullWidth={true}
          onBlur={(e) => handleDollarInputBlur("deductible", e.target.value)}
          onChange={(e) => {
            handleDollarInputChange(
              "deductible",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the coinsurance input field.
   * @returns {JSX.Element} Coinsurance input field.
   */
  const renderCoinsuranceInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={
            order?.insuranceTypeId === 4
              ? "N/A - Cash"
              : (approval?.coInsurance ?? "")
          }
          size="small"
          label="Coinsurance"
          //Cash
          disabled={order?.insuranceTypeId === 4}
          variant="standard"
          fullWidth={true}
          onBlur={(e) => handleDollarInputBlur("coInsurance", e.target.value)}
          onChange={(e) => {
            handleDollarInputChange(
              "coInsurance",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the patient responsibility input field.
   * @returns {JSX.Element} Patient responsibility input field.
   */
  const renderPatientResponsibilityInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={approval?.patientResponsibility ?? ""}
          size="small"
          label="Patient Responsibility"
          variant="standard"
          fullWidth={true}
          onBlur={(e) =>
            handleDollarInputBlur("patientResponsibility", e.target.value)
          }
          onChange={(e) => {
            handleDollarInputChange(
              "patientResponsibility",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the state sales tax input field.
   * @returns {JSX.Element} State sales tax input field.
   */
  const renderStateSalesTaxInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={approval?.stateSalesTax ?? ""}
          size="small"
          label="Sales Tax"
          variant="standard"
          fullWidth={true}
          onBlur={(e) => handleDollarInputBlur("stateSalesTax", e.target.value)}
          onChange={(e) => {
            handleDollarInputChange(
              "stateSalesTax",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the payment on delivery input field.
   * @returns {JSX.Element} Payment on delivery input field.
   */
  const renderPaymentOnDeliveryInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={approval?.paymentOnDelivery ?? ""}
          size="small"
          label="Payment On Delivery"
          variant="standard"
          fullWidth={true}
          onBlur={(e) =>
            handleDollarInputBlur("paymentOnDelivery", e.target.value)
          }
          onChange={(e) => {
            handleDollarInputChange(
              "paymentOnDelivery",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the payment collected input field.
   * @returns {JSX.Element} Payment collected input field.
   */
  const renderPaymentCollectedInput = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <MUITextField
          value={approval?.paymentCollected ?? ""}
          size="small"
          label="Payment Collected"
          variant="standard"
          fullWidth={true}
          onBlur={(e) =>
            handleDollarInputBlur("paymentCollected", e.target.value)
          }
          onChange={(e) => {
            handleDollarInputChange(
              "paymentCollected",
              sanitizeInputDollarValue(e.target.value),
            );
          }}
          slotProps={{
            input: {
              startAdornment: (
                <MUIInputAdornment position="start">$</MUIInputAdornment>
              ),
            },

            htmlInput: {
              maxLength: 255,
              style: { textAlign: "right" },
            },

            inputLabel: { shrink: true },
          }}
        />
      </MUIGrid>
    );
  };

  /**
   * Renders the edit view displaying patient financial information.
   * @returns {JSX.Element} Edit view grid layout.
   */
  const renderEditView = () => {
    return (
      <MUIGrid
        container
        spacing={4}
        rowSpacing={3}
        sx={{ containerType: "inline-size" }}
      >
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            xs: 12,
            md: props.sidebar ? 12 : 12,
            lg: props.sidebar ? 12 : 8,
          }}
        >
          <MUIGrid
            container
            spacing={2}
            rowSpacing={3}
            sx={{ containerType: "inline-size" }}
          >
            {renderDeductibleInput()}
            {renderCoinsuranceInput()}
            {renderPatientResponsibilityInput()}
            {renderStateSalesTaxInput()}
            {renderPaymentOnDeliveryInput()}
            {renderPaymentCollectedInput()}
            <MUIGrid size={12}>
              <MUIFormControlLabel
                control={
                  <MUICheckbox
                    checked={!!approval?.paymentProcessedOn}
                    disabled={
                      !approval?.patientResponsibility ||
                      parseFloat(approval.patientResponsibility) <= 0
                    }
                    onChange={(e) => {
                      chartContext.onChange({
                        approval: {
                          [approvalId]: {
                            ...approval,
                            paymentProcessedOn: e.target.checked
                              ? new Date().toISOString()
                              : null,
                            paymentProcessedById: e.target.checked
                              ? mainContext.currentUser.id
                              : null,
                          },
                        },
                      });
                    }}
                  />
                }
                label="Confirm Payment Processed"
              />
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
        <MUIGrid
          sx={getContainerQuery(600)}
          size={{
            xs: 12,
            md: props.sidebar ? 12 : 12,
            lg: props.sidebar ? 12 : 4,
          }}
        >
          <MUIGrid
            container
            spacing={2}
            rowSpacing={3}
            sx={{ containerType: "inline-size" }}
          >
            <MUIGrid
              sx={getContainerQuery(600)}
              size={{
                xs: 12,
                md: props.sidebar ? 12 : 6,
                lg: 12,
              }}
            >
              <MUIButton
                variant="outlined"
                color="primary"
                startIcon={<MUICalculateIcon />}
                onClick={() =>
                  setOpenDialogPatientResponsibilityEstimator(true)
                }
                size="small"
              >
                Estimator
              </MUIButton>
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying patient financial information.
   * @returns {JSX.Element} Read-only view grid layout.
   */
  const renderReadOnlyView = () => {
    // Helper to format dollar amounts
    const formatUSD = (value) => {
      if (value == null || value === "") {
        return "";
      }
      const numberValue = Number(value);
      if (isNaN(numberValue)) {
        return "";
      }
      return numberValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    };

    return (
      <MUIGrid container spacing={props.sidebar ? 1 : 4} rowSpacing={2}>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            label={"Deductible"}
            value={
              order?.insuranceTypeId === 4
                ? "N/A - Cash"
                : formatUSD(approval?.deductible)
            }
          />
        </MUIGrid>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            label={"Coinsurance"}
            value={
              order?.insuranceTypeId === 4
                ? "N/A - Cash"
                : formatUSD(approval?.coInsurance)
            }
          />
        </MUIGrid>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            label={"Patient Responsibility"}
            value={formatUSD(approval?.patientResponsibility)}
          />
        </MUIGrid>
        {approval?.stateSalesTax != null && (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              label={"Sales Tax"}
              value={formatUSD(approval.stateSalesTax)}
            />
          </MUIGrid>
        )}
        {approval?.paymentOnDelivery != null && (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              label={"Payment on Delivery"}
              value={formatUSD(approval.paymentOnDelivery)}
            />
          </MUIGrid>
        )}
        {approval?.paymentCollected != null && (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              label={"Payment Collected"}
              value={formatUSD(approval.paymentCollected)}
            />
          </MUIGrid>
        )}
        {approval?.paymentProcessedOn != null && (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              label={"Payment Processed On"}
              value={Format.date(approval.paymentProcessedOn)}
            />
          </MUIGrid>
        )}
      </MUIGrid>
    );
  };

  /**
   * Renders either the edit view or read-only view based on edit mode state.
   * Also handles dialog for patient responsibility estimator.
   * @returns {JSX.Element} Content for the card.
   */
  const renderContent = () => {
    const formatInputValues = (value) => {
      if (value != null) {
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        return "";
      }
    };

    /**
     * Handles submission from the patient responsibility estimator dialog.
     * Updates approval fields with calculated values.
     * @param {number|string} coinsurance - Calculated coinsurance value.
     * @param {number|string} deductible - Calculated deductible value.
     * @param {number|string} patientResponsibility - Calculated patient
     * responsibility value.
     */
    const handleSubmitPatientResponsibilityEstimator = (
      coinsurance,
      deductible,
      patientResponsibility,
      updatedRowAmounts,
      isDownloadGoodFaithEstimate,
    ) => {
      const updatedApproval = {
        ...approval,
        coInsurance: coinsurance,
        deductible: deductible,
        patientResponsibility: patientResponsibility,
      };

      if (updatedRowAmounts.length > 0) {
        const productUpdates = updatedRowAmounts.reduce(
          (accumulator, updatedRowAmount) => {
            accumulator[updatedRowAmount.id] = {
              amount: updatedRowAmount.amount,
            };
            return accumulator;
          },
          {},
        );

        chartContext.onChange({
          approval: { [approvalId]: updatedApproval },
          product: productUpdates,
        });
      } else {
        chartContext.onChange({
          approval: { [approvalId]: updatedApproval },
        });
      }

      setShouldSave({
        shouldSave: true,
        shouldDownloadGoodFaithEstimate: isDownloadGoodFaithEstimate,
      });
    };

    if (editMode) {
      const patientResponsibilityRows = getPatientResponsibilityRows();

      return (
        <>
          {renderEditView()}
          <DialogPatientResponsibilityEstimator
            open={openDialogPatientResponsibilityEstimator}
            onClose={() => {
              setShouldSave({
                shouldSave: false,
                shouldDownloadGoodFaithEstimate: false,
              });
              setOpenDialogPatientResponsibilityEstimator(false);
            }}
            isUpdatingChart={chartContext.isUpdating}
            rows={patientResponsibilityRows}
            order={order}
            deductible={approval?.deductible}
            coInsurance={approval?.coInsurance}
            onConfirm={(
              coinsurance,
              deductible,
              patientResponsibility,
              updatedRowAmounts,
              isDownloadGoodFaithEstimate,
            ) => {
              handleSubmitPatientResponsibilityEstimator(
                coinsurance,
                deductible,
                patientResponsibility,
                updatedRowAmounts,
                isDownloadGoodFaithEstimate,
              );
            }}
          />
        </>
      );
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component (Edit/Cancel).
   * @returns {Array<JSX.Element>} Array of action button components.
   */
  const getActions = () => {
    /**
     * Handles cancel action, reverting changes to approval fields and
     * clearing validation issues.
     */
    const handleCancel = () => {
      const updatedApproval = { ...approval };
      const financialFields = cardChart ? Object.keys(cardChart) : [];

      for (const field of financialFields) {
        if (approval[field] !== originalApproval[field]) {
          updatedApproval[field] = originalApproval[field];
        }
      }
      chartContext.onChange({
        approval: { [approvalId]: { ...updatedApproval } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    };

    return [
      ...(editMode
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
          ]
        : [
            <MUIButton
              disabled={chartContext.isUpdating}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Edit
            </MUIButton>,
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartFinancial.meta = { className: className, title: title, icon: icon };
